import { useState } from "react"
import { useTheme } from "next-themes"

import ProductPlaceholderSmall from "~/images/image_placeholder_small.png"
import ProductPlaceholderSmallDark from "~/images/image_placeholder_small_dark.png"
import { defaultWidths, ensureHttps, getClosestWidth, resolveLoader } from "~/utils/imageLoaders"

type Fit = "cover" | "contain" | "fill" | "none" | "scale-down"

interface ImageProps {
  src: string | undefined
  alt: string
  width: number
  height: number
  sizes?: string
  className?: string
  objectFit?: Fit
  placeholderSrc?: string
  quality?: string
  priority?: boolean
}

// Sources loaded this session — re-mounts skip the fade-in so cached images don't re-flicker.
const loadedSources = new Set<string>()

export const Image = ({
  src,
  alt,
  width,
  height,
  sizes = "(max-width: 639px) 32px, (max-width: 767px) 64px, (max-width: 1023px) 128px, 256px",
  className,
  objectFit = "cover",
  placeholderSrc,
  quality = "75",
  priority = false,
}: ImageProps) => {
  const { resolvedTheme } = useTheme()
  const [hasError, setHasError] = useState(false)
  const [loaded, setLoaded] = useState(() => (src ? loadedSources.has(src) : false))

  const entry = resolveLoader(src ?? "")
  const closestWidth = getClosestWidth(width)
  const shouldUseFallback = hasError || !src

  const fallbackSrc =
    resolvedTheme === "dark" ? ProductPlaceholderSmallDark : ProductPlaceholderSmall
  const httpsSrc = shouldUseFallback ? "" : ensureHttps(src)
  const processedSrc = entry.transform ? entry.transform(httpsSrc) : httpsSrc
  const actualSrc = shouldUseFallback
    ? fallbackSrc
    : entry.loader(processedSrc, closestWidth, quality)

  const srcSet = shouldUseFallback
    ? ""
    : defaultWidths.map((w) => `${entry.loader(processedSrc, w, quality)} ${w}w`).join(", ")

  const webpSrcSet = shouldUseFallback
    ? ""
    : defaultWidths.map((w) => `${entry.loader(processedSrc, w, quality, "webp")} ${w}w`).join(", ")
  const avifSrcSet =
    shouldUseFallback || entry.suppressAvif
      ? ""
      : defaultWidths
          .map((w) => `${entry.loader(processedSrc, w, quality, "avif")} ${w}w`)
          .join(", ")

  return (
    <div
      className={`relative overflow-hidden ${className ?? ""}`}
      style={{ width, height, aspectRatio: `${width} / ${height}` }}
    >
      {placeholderSrc && !loaded && (
        <img
          src={placeholderSrc}
          alt="placeholder"
          className="absolute inset-0 w-full h-full object-cover blur-sm scale-105"
        />
      )}

      <picture>
        {!shouldUseFallback && (
          <>
            {!entry.suppressAvif && <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />}
            <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
          </>
        )}
        <img
          src={actualSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          onLoad={() => {
            if (src) loadedSources.add(src)
            setLoaded(true)
          }}
          onError={() => setHasError(true)}
          className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
            !loaded ? "opacity-0" : "opacity-100"
          } object-${objectFit}`}
        />
      </picture>
    </div>
  )
}
